/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';

import { CheckboxFieldsWrapper, CheckboxField, Checkbox, CheckboxInput, CheckboxInputLabel } from './checkboxes.styled';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { ControlType, ctrlDefaults } from '../../../../shared/form-lib';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { controlValidate, getStateValue } from '../../../form/utils';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { getEmptyTableFields } from '../../../../shared/utils';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import OtherCheckbox from './other_checkbox';
import {
  ValidationWarning,
  MainChoiceWrapper,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  Field,
} from '../../common-field-styles';

const CheckBox = ({ ctrl, inTable, tr, col, formDesign }) => {
  const dispatch = useDispatch();
  const { inCustomise, inBuilder, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const responseStore = form?.response_store ?? 'formplus';
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const [newCtrl, setNewCtrl] = useState(ctrl);
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  const saveUpdatedCtrl = (property, newValue, isValid) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property,
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid,
        ...(inTable && { tr: tr - 1, col: col - 1 }),
      })
    );
  };

  const handleOptionChange = (option, otherText) => {
    let newArr = [];
    if (inTable) {
      const isColVal = ctrl.value && Array.isArray(ctrl.value[tr - 1][col - 1]);
      const newVal = isColVal ? [...ctrl.value[tr - 1][col - 1]] : [];
      if (newVal.includes(option)) {
        const index = newVal.indexOf(option);
        newArr = newVal.filter((item) => item !== newVal[index]);
      } else {
        newArr = [...newVal, option];
      }
      const control = cloneDeep(newCtrl);
      if (!newCtrl.value) {
        control.value = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
        control._validationError = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
        control.value[tr - 1][col - 1] = newArr;
        setNewCtrl(control);
      } else {
        control.value[tr - 1][col - 1] = newArr;
        setNewCtrl(control);
      }
      controlValidate({
        isPayOnSubmission: false,
        setCtrlValidationError,
        userCustomTexts,
        responseStore,
        ctrl: control,
        setNewCtrl,
        col,
        tr
      });
      saveUpdatedCtrl('value', newArr);
    } else {
      let newVal = cloneDeep(ctrl.value) || {};

      if (option === ControlType.OptOther.STRING) {
        if (otherText) newVal[option] = otherText;
        else delete newVal[option];
      } else {
        if (newVal[option]) delete newVal[option];
        else newVal[option] = true;
      }

      if (Object.keys(newVal).length === 0) newVal = null;
      setNewCtrl((prevCtrl) => ({ ...cloneDeep(prevCtrl), value: newVal }));

      const { validationOk } = controlValidate({
        ctrl: { ...cloneDeep(ctrl), value: newVal },
        isPayOnSubmission: false,
        setCtrlValidationError,
        userCustomTexts,
        responseStore,
        setNewCtrl,
        col,
        tr
      });

      saveUpdatedCtrl('value', newVal, validationOk);
    }
  };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
          {!inTable && (
            <>
              <LabelWrapper
                notInBuilder={!inBuilder}
                required={ctrl.props.required}
                className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
                <label
                  htmlFor={`fpl-${ctrl.internal_id}`}
                  dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
                />
                <SpecialPropsIndicators ctrl={ctrl} />
              </LabelWrapper>

              <MainChoiceWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
                <CheckboxFieldsWrapper
                  className={ctrl.props.inline ? 'inline' : ''}
                  id={`fpl-${ctrl.internal_id}`}
                  inForm>
                  {ctrl.props.options.map((option, index) => (
                    <CheckboxField key={option}>
                      <Checkbox>
                        <CheckboxInput
                          type="checkbox"
                          id={`fpl-${ctrl.internal_id}-${index}`}
                          checked={ctrl.value?.[option] || false}
                          name={`fpl-${ctrl.internal_id}-${index}`}
                          onChange={() => handleOptionChange(option, false)}
                          disabled={ctrl.props.readonly || inCustomise || inBuilder}
                          title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                        />

                        <CheckboxInputLabel
                          notInBuilder={!inBuilder}
                          htmlFor={`fpl-${ctrl.internal_id}-${index}`}
                          title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                          className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}>
                          {option}
                        </CheckboxInputLabel>
                      </Checkbox>
                    </CheckboxField>
                  ))}

                  {ctrl.props.other && ctrl.props.other.enabled && (
                    <OtherCheckbox
                      ctrl={ctrl}
                      saveUpdatedCtrl={saveUpdatedCtrl}
                      handleOptionChange={handleOptionChange}
                    />
                  )}
                </CheckboxFieldsWrapper>

                <SubtextWrapper notInBuilder={!inBuilder}>{ctrl.props.subtext}</SubtextWrapper>
              </MainChoiceWrapper>
            </>
          )}
          {inTable && (
            <>
              <MainChoiceWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
                <CheckboxFieldsWrapper className={ctrl.table_props[col - 1].inline ? 'inline' : ''} inForm>
                  {ctrl.table_props[col - 1].options.map((option, index) => (
                    <CheckboxField key={option}>
                      <Checkbox>
                        <CheckboxInput
                          type="checkbox"
                          required={ctrl.table_props[col - 1].required}
                          onChange={() => handleOptionChange(option, false)}
                          id={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                          name={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                          checked={ctrl.value?.[tr - 1]?.[col - 1]?.includes(option) || false}
                          disabled={ctrl.table_props[col - 1].readonly || inCustomise || inBuilder}
                          title={ctrl.table_props[col - 1].readonly ? 'This field cannot be edited' : ''}
                        />

                        <CheckboxInputLabel
                          notInBuilder={!inBuilder}
                          htmlFor={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                          title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                          className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}>
                          {option}
                        </CheckboxInputLabel>
                      </Checkbox>
                    </CheckboxField>
                  ))}
                </CheckboxFieldsWrapper>
              </MainChoiceWrapper>
            </>
          )}
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

CheckBox.defaultProps = {
  col: null,
  tr: null,
  formDesign: null,
  inTable: false,
};

CheckBox.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  col: PropTypes.string,
  tr: PropTypes.string,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default CheckBox;
